import React, { useState, useRef, useCallback } from 'react';
import { Icon, Button } from 'semantic-ui-react';
import './CanvasItem.css';

const CanvasItem = ({
    item,
    index,
    isSelected,
    isMultiSelected,
    onSelect,
    onMove,
    onResize,
    onRotate,
    onMoveStart,
    onMoveEnd,
    onResizeStart,
    onResizeEnd,
    onRotateStart,
    onRotateEnd,
    onRevertToOriginal,
    onRetryVirtualTryOn
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, type: '' });
    const [rotateStart, setRotateStart] = useState(0);

    const itemRef = useRef(null);
    const isAnySelected = isSelected || isMultiSelected;

    // Track if we actually dragged (to distinguish from clicks)
    const hasDraggedRef = useRef(false);
    const dragThreshold = 5; // pixels
    const mouseDownPositionRef = useRef(null);

    // Handle item click for selection
    const handleClick = useCallback((e) => {
        e.stopPropagation();

        // Don't handle click if we're in the middle of an operation
        if (isResizing || isRotating) {
            return;
        }

        // Don't handle click if we just finished dragging
        if (hasDraggedRef.current) {
            return;
        }

        onSelect(index, e);
    }, [index, onSelect, isResizing, isRotating]);

    // Get client coordinates from mouse or touch event
    const getClientCoords = (e) => {
        if (e.touches && e.touches.length > 0) {
            return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
        }
        return { clientX: e.clientX, clientY: e.clientY };
    };

    // Handle drag start - only allow if item is selected
    const handleMouseDown = useCallback((e) => {
        // Only handle drag if clicking on the image itself, not on handles
        if (e.target.classList.contains('resize-handle') ||
            e.target.classList.contains('rotate-handle') ||
            e.target.closest('.resize-handle') ||
            e.target.closest('.rotate-handle')) {
            return;
        }

        e.stopPropagation();

        // Only allow dragging if the item is selected (single or multi)
        if (!isAnySelected) {
            // If item is not selected, this will be handled by the click event
            return;
        }

        const { clientX, clientY } = getClientCoords(e);

        // Store the initial position for click vs drag detection
        mouseDownPositionRef.current = { x: clientX, y: clientY };

        // Don't set isDragging immediately - wait for actual movement
        hasDraggedRef.current = false;
        setDragStart({ x: clientX - item.x, y: clientY - item.y });

        if (onMoveStart) {
            onMoveStart(e, index);
        }
    }, [item.x, item.y, index, onMoveStart, isAnySelected]);

    // Handle resize start
    const handleResizeStart = useCallback((e, resizeType) => {
        e.stopPropagation();
        e.preventDefault(); // Prevent default touch behavior
        
        const { clientX, clientY } = getClientCoords(e);
        
        setIsResizing(true);
        setResizeStart({
            x: clientX,
            y: clientY,
            width: item.width,
            height: item.height,
            itemX: item.x,
            itemY: item.y,
            type: resizeType
        });

        if (onResizeStart) {
            onResizeStart(e, index, resizeType);
        }
    }, [item.width, item.height, item.x, item.y, index, onResizeStart]);

    // Handle rotate start
    const handleRotateStart = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault(); // Prevent default touch behavior
        
        const { clientX, clientY } = getClientCoords(e);
        
        setIsRotating(true);

        const rect = itemRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const angle = Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
        setRotateStart(angle - (item.rotation || 0));

        if (onRotateStart) {
            onRotateStart(e, index);
        }
    }, [item.rotation, index, onRotateStart]);

    // Global mouse/touch move handler
    const handleMouseMove = useCallback((e) => {
        const { clientX, clientY } = getClientCoords(e);
        
        // Check if we should start dragging based on movement threshold
        if (dragStart && !isDragging && !isResizing && !isRotating) {
            const deltaX = Math.abs(clientX - (dragStart.x + item.x));
            const deltaY = Math.abs(clientY - (dragStart.y + item.y));
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (distance > dragThreshold) {
                setIsDragging(true);
                hasDraggedRef.current = true;
            }
        }

        if (isDragging && onMove && dragStart) {
            const newX = clientX - dragStart.x;
            const newY = clientY - dragStart.y;
            onMove(index, { x: newX, y: newY });
        } else if (isResizing && onResize) {
            const deltaX = clientX - resizeStart.x;
            const deltaY = clientY - resizeStart.y;

            let newWidth = resizeStart.width;
            let newHeight = resizeStart.height;
            let newX = resizeStart.itemX;
            let newY = resizeStart.itemY;

            const aspectRatio = resizeStart.height / resizeStart.width;

            switch (resizeStart.type) {
                case 'proportional':
                    newWidth = Math.max(50, resizeStart.width + deltaX);
                    newHeight = newWidth * aspectRatio;
                    break;
                
                // Edge resize handles (non-proportional)
                case 'left':
                    newWidth = Math.max(50, resizeStart.width - deltaX);
                    newHeight = resizeStart.height; // Keep original height
                    newX = resizeStart.itemX + (resizeStart.width - newWidth);
                    break;
                case 'right':
                    newWidth = Math.max(50, resizeStart.width + deltaX);
                    newHeight = resizeStart.height; // Keep original height
                    break;
                case 'top':
                    newWidth = resizeStart.width; // Keep original width
                    newHeight = Math.max(50, resizeStart.height - deltaY);
                    newY = resizeStart.itemY + (resizeStart.height - newHeight);
                    break;
                case 'bottom':
                    newWidth = resizeStart.width; // Keep original width
                    newHeight = Math.max(50, resizeStart.height + deltaY);
                    break;
                
                // Corner resize handles (proportional)
                case 'top-left':
                    newWidth = Math.max(50, resizeStart.width - deltaX);
                    newHeight = newWidth * aspectRatio;
                    newX = resizeStart.itemX + (resizeStart.width - newWidth);
                    newY = resizeStart.itemY + (resizeStart.height - newHeight);
                    break;
                case 'top-right':
                    newWidth = Math.max(50, resizeStart.width + deltaX);
                    newHeight = newWidth * aspectRatio;
                    newY = resizeStart.itemY + (resizeStart.height - newHeight);
                    break;
                case 'bottom-left':
                    newWidth = Math.max(50, resizeStart.width - deltaX);
                    newHeight = newWidth * aspectRatio;
                    newX = resizeStart.itemX + (resizeStart.width - newWidth);
                    break;
                case 'bottom-right':
                    newWidth = Math.max(50, resizeStart.width + deltaX);
                    newHeight = newWidth * aspectRatio;
                    break;
                default:
                    break;
            }

            onResize(index, { width: newWidth, height: newHeight, x: newX, y: newY });
        } else if (isRotating && onRotate) {
            const rect = itemRef.current.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            const angle = Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
            let rotation = angle - rotateStart;

            // Snap to 45-degree increments when holding Shift
            if (e.shiftKey) {
                rotation = Math.round(rotation / 45) * 45;
            }

            onRotate(index, rotation);
        }
    }, [isDragging, isResizing, isRotating, dragStart, resizeStart, rotateStart, index, item.x, onMove, onResize, onRotate]);

    // Global mouse up handler
    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            if (onMoveEnd) {
                onMoveEnd(index);
            }
        } else if (isResizing) {
            setIsResizing(false);
            if (onResizeEnd) {
                onResizeEnd(index);
            }
        } else if (isRotating) {
            setIsRotating(false);
            if (onRotateEnd) {
                onRotateEnd(index);
            }
        }

        // Reset drag start to null
        setDragStart(null);

        // Clear the drag flag and mouse position after a short delay
        // Only clear if we actually dragged
        if (isDragging || isResizing || isRotating) {
            setTimeout(() => {
                hasDraggedRef.current = false;
                mouseDownPositionRef.current = null;
            }, 100);
        } else {
            // If no actual drag/resize/rotate happened, clear immediately
            hasDraggedRef.current = false;
            mouseDownPositionRef.current = null;
        }
    }, [isDragging, isResizing, isRotating, index, onMoveEnd, onResizeEnd, onRotateEnd]);

    // Add global event listeners for both mouse and touch
    React.useEffect(() => {
        if (isDragging || isResizing || isRotating || dragStart !== null) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleMouseMove, { passive: false });
            document.addEventListener('touchend', handleMouseUp);
            document.addEventListener('touchcancel', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleMouseMove);
                document.removeEventListener('touchend', handleMouseUp);
                document.removeEventListener('touchcancel', handleMouseUp);
            };
        }
    }, [isDragging, isResizing, isRotating, dragStart, handleMouseMove, handleMouseUp]);

    return (
        <div
            ref={itemRef}
            className={`canvas-item ${isSelected ? 'selected' : ''} ${isMultiSelected ? 'multi-selected' : ''} ${item.isVirtualTryOn ? 'virtual-tryon-item' : ''}`}
            style={{
                position: 'absolute',
                left: `${item.x}px`,
                top: `${item.y}px`,
                width: `${item.width}px`,
                height: `${item.height}px`,
                transform: `rotate(${item.rotation || 0}deg)`,
                zIndex: item.zIndex || 1,
                cursor: isDragging ? 'grabbing' : (isAnySelected ? 'grab' : 'pointer')
            }}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
        >
            {/* Selection indicator overlay */}
            {isAnySelected && (
                <div className={`selection-overlay ${isSelected ? 'single-selected' : 'multi-selected'}`}>
                    <div className="selection-border"></div>
                    {isMultiSelected && (
                        <div className="multi-select-indicator">
                            <Icon name="check circle" size="small" />
                        </div>
                    )}
                </div>
            )}

            {/* Main image */}
            <img
                src={typeof item.image === 'string' ? item.image : item.image?.url || item.image}
                className={`item-image ${item.isProportional ? 'proportional' : ''}`}
                alt={item.name || item.clothesName || 'Clothing item'}
                draggable="false"
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    pointerEvents: 'none'
                }}
            />

            {/* Virtual try-on badges */}
            {item.isVirtualTryOn && (
                <>
                    <div className="virtual-tryon-badge">
                        <Icon name="magic" size="small" />
                    </div>
                    {item.tryOnMetadata && item.tryOnMetadata.quality && (
                        <div className={`virtual-tryon-quality ${item.tryOnMetadata.quality}`}>
                            {item.tryOnMetadata.quality === 'excellent' ? 'A' :
                                item.tryOnMetadata.quality === 'good' ? 'B' :
                                    item.tryOnMetadata.quality === 'fair' ? 'C' : 'D'}
                        </div>
                    )}
                </>
            )}

            {/* Virtual try-on controls - only show for single selected items */}
            {isSelected && item.isVirtualTryOn && (
                <div className="virtual-tryon-controls-overlay">
                    <Button
                        size="mini"
                        icon="undo"
                        title="Revert to original"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRevertToOriginal(index);
                        }}
                    />
                    <Button
                        size="mini"
                        icon="refresh"
                        title="Retry virtual try-on"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRetryVirtualTryOn(index);
                        }}
                    />
                </div>
            )}

            {/* Resize and rotate handles - show for any selected items */}
            {isAnySelected && (
                <>
                    {/* Corner resize handles (proportional) */}
                    <div
                        className="resize-handle corner top-left"
                        onMouseDown={(e) => handleResizeStart(e, 'top-left')}
                        onTouchStart={(e) => handleResizeStart(e, 'top-left')}
                    />
                    <div
                        className="resize-handle corner top-right"
                        onMouseDown={(e) => handleResizeStart(e, 'top-right')}
                        onTouchStart={(e) => handleResizeStart(e, 'top-right')}
                    />
                    <div
                        className="resize-handle corner bottom-left"
                        onMouseDown={(e) => handleResizeStart(e, 'bottom-left')}
                        onTouchStart={(e) => handleResizeStart(e, 'bottom-left')}
                    />
                    <div
                        className="resize-handle corner bottom-right"
                        onMouseDown={(e) => handleResizeStart(e, 'bottom-right')}
                        onTouchStart={(e) => handleResizeStart(e, 'bottom-right')}
                    />

                    {/* Edge resize handles */}
                    <div
                        className="resize-handle edge left"
                        onMouseDown={(e) => handleResizeStart(e, 'left')}
                        onTouchStart={(e) => handleResizeStart(e, 'left')}
                    />
                    <div
                        className="resize-handle edge right"
                        onMouseDown={(e) => handleResizeStart(e, 'right')}
                        onTouchStart={(e) => handleResizeStart(e, 'right')}
                    />
                    <div
                        className="resize-handle edge top"
                        onMouseDown={(e) => handleResizeStart(e, 'top')}
                        onTouchStart={(e) => handleResizeStart(e, 'top')}
                    />
                    <div
                        className="resize-handle edge bottom"
                        onMouseDown={(e) => handleResizeStart(e, 'bottom')}
                        onTouchStart={(e) => handleResizeStart(e, 'bottom')}
                    />

                    {/* Rotation handle */}
                    <div
                        className="rotate-handle"
                        onMouseDown={handleRotateStart}
                        onTouchStart={handleRotateStart}
                    />
                </>
            )}
        </div>
    );
};

export default CanvasItem;